import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import {
	faBolt,
	faBookOpen,
	faCalculator,
	faCalendarAlt,
	faChalkboardTeacher,
	faClock,
	faCogs,
	faCoins,
	faDiceD20,
	faDungeon,
	faEuroSign,
	faGamepad,
	faHeartCrack,
	faHourglass,
	faHourglassHalf,
	faInfinity,
	faLink,
	faMountain,
	faQuoteRight,
	faRoute,
	faScroll,
	faTag,
	faUserAlt,
	faWallet,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { type Transition, motion } from "framer-motion";
import { useState } from "react";

// Add this type definition
// Update the AnimationConfig type to match Framer Motion's expectations
type AnimationConfig = {
	rotate?: number | number[];
	rotateY?: number | number[];
	rotateX?: number | number[];
	scale?: number | number[];
	x?: number | number[];
	y?: number | number[];
	color?: string;
	transition?: Transition;
};

type IconAnimation = {
	icon: IconDefinition;
	animation: AnimationConfig;
};

const HOVER_COLOR = "#f93d3d"; // Red color for hover state

export function assignIconAndAnimation(phrase: string): IconAnimation {
	const iconMappings = [
		// Money-related
		{
			keywords: ["€"],
			icon: faEuroSign,
			animation: {
				y: [0, -5, 0],
				color: HOVER_COLOR,
				transition: { repeat: Infinity, duration: 0.8 },
			},
		},
		{
			keywords: ["precio"],
			icon: faTag,
			animation: {
				rotate: [-5, 5, -5],
				color: HOVER_COLOR,
				transition: { repeat: Infinity, duration: 0.5 },
			},
		},
		{
			keywords: ["presupuesto"],
			icon: faWallet,
			animation: {
				x: [0, -5, 0],
				color: HOVER_COLOR,
				transition: { duration: 0.5 },
			},
		},
		{
			keywords: ["total"],
			icon: faCalculator,
			animation: {
				scale: [1, 1.1, 1],
				color: HOVER_COLOR,
				transition: { duration: 0.7 },
			},
		},
		// Time-related
		{
			keywords: ["horas"],
			icon: faClock,
			animation: {
				rotate: 10,
				color: HOVER_COLOR,
				transition: { duration: 0.3 },
			},
		},
		{
			keywords: ["sesión", "sesiones"],
			icon: faHourglassHalf,
			animation: {
				rotateY: 180,
				color: HOVER_COLOR,
				transition: { duration: 0.5 },
			},
		},

		{
			keywords: ["continua"],
			icon: faInfinity,
			animation: {
				scale: [1, 1.2, 1],
				color: HOVER_COLOR,
				transition: { repeat: Infinity, duration: 1.5 },
			},
		},
		{
			keywords: ["largas"],
			icon: faCalendarAlt,
			animation: {
				y: [0, -5],
				color: HOVER_COLOR,
				transition: { duration: 0.5 },
			},
		},

		// Game elements
		{
			keywords: ["personajes", "personaje"],
			icon: faUserAlt,
			animation: {
				rotate: [0, 360],
				color: HOVER_COLOR,
				transition: { duration: 1 },
			},
		},
		{
			keywords: ["fichas"],
			icon: faCoins,
			animation: {
				y: [0, -5, 0],
				color: HOVER_COLOR,
				transition: { repeat: Infinity, duration: 0.6 },
			},
		},
		{
			keywords: ["dados"],
			icon: faDiceD20,
			animation: {
				rotate: [0, 360],
				color: HOVER_COLOR,
				transition: { duration: 1.5 },
			},
		},
		{
			keywords: ["aventura", "aventuras"],
			icon: faMountain,
			animation: {
				y: [0, -5],
				color: HOVER_COLOR,
				transition: { duration: 0.7 },
			},
		},
		{
			keywords: ["one-shot"],
			icon: faBolt,
			animation: {
				scale: [1, 1.2, 1],
				color: HOVER_COLOR,
				transition: { duration: 0.5 },
			},
		},
		{
			keywords: ["trama"],
			icon: faBookOpen,
			animation: {
				rotateY: [0, 180],
				color: HOVER_COLOR,
				transition: { duration: 0.8 },
			},
		},
		{
			keywords: ["giros"],
			icon: faRoute,
			animation: {
				scale: [1, 1.1, 1],
				color: HOVER_COLOR,
				transition: { repeat: Infinity, duration: 1.2 },
			},
		},
		{
			keywords: ["reglas"],
			icon: faScroll,
			animation: {
				x: [0, -5, 0],
				color: HOVER_COLOR,
				transition: { duration: 0.6 },
			},
		},
		{
			keywords: ["mecánicas"],
			icon: faCogs,
			animation: {
				rotate: [0, 90],
				color: HOVER_COLOR,
				transition: { duration: 2, repeat: Infinity },
			},
		},
		{
			keywords: ["juego", "jugar"],
			icon: faGamepad,
			animation: {
				y: [0, -3, 0],
				color: HOVER_COLOR,
				transition: { repeat: Infinity, duration: 0.5 },
			},
		},
		{
			keywords: ["durante"],
			icon: faHourglass,
			animation: {
				rotateX: [0, 180],
				color: HOVER_COLOR,
				transition: { duration: 1 },
			},
		},

		// Special terms
		{
			keywords: ["coherente"],
			icon: faLink,
			animation: {
				scale: [1, 1.1, 1],
				color: HOVER_COLOR,
				transition: { repeat: Infinity, duration: 0.8 },
			},
		},
		{
			keywords: ["impactantes"],
			icon: faBolt,
			animation: {
				scale: [0.9, 1.1, 0.9],
				color: HOVER_COLOR,
				transition: { repeat: Infinity, duration: 0.7 },
			},
		},
		{
			keywords: ["explicadas"],
			icon: faChalkboardTeacher,
			animation: {
				x: [0, -5, 0],
				color: HOVER_COLOR,
				transition: { duration: 0.6 },
			},
		},
		{
			keywords: ["contexto"],
			icon: faQuoteRight,
			animation: {
				scale: [1, 1.1, 1],
				color: HOVER_COLOR,
				transition: { repeat: Infinity, duration: 1 },
			},
		},
		{
			keywords: ["broken", "heartbreak"],
			icon: faHeartCrack,
			animation: {
				scale: [1, 1.2, 1],
				rotate: [0, 10, -10, 0],
				color: HOVER_COLOR,
				transition: { duration: 0.8 },
			},
		},
	];

	const lowerPhrase = phrase.toLowerCase();
	const matchingIcons = [];

	for (const mapping of iconMappings) {
		for (const keyword of mapping.keywords) {
			if (lowerPhrase.includes(keyword)) {
				matchingIcons.push({
					icon: mapping.icon,
					animation: mapping.animation,
				});
				break;
			}
		}
	}

	// Update the default animation in assignIconAndAnimation
	if (matchingIcons.length === 0) {
		return {
			icon: faDungeon,
			animation: {
				scale: 1.1,
				color: HOVER_COLOR,
				transition: { duration: 0.8, type: "spring" },
			},
		};
	}

	// Find the first matching icon configuration
	const foundMapping = iconMappings.find((mapping) =>
		mapping.keywords.some((keyword) => lowerPhrase.includes(keyword)),
	);

	// Return either the found mapping or the default one
	return foundMapping
		? {
				icon: foundMapping.icon,
				animation: foundMapping.animation,
			}
		: {
				icon: faDungeon,
				animation: {
					scale: 1.1,
					color: HOVER_COLOR,
					transition: { duration: 0.8 },
				},
			};
}

// First, create a utility function to handle the bold formatting tes
const formatFeatureText = (text: string) => {
	return text.split("**").map((part, index) => {
		return index % 2 === 1 ? <strong key={index}>{part}</strong> : <span key={index}>{part}</span>;
	});
};
export function IconWithAnimation(params: { phrase: string; itemKey: number }) {
	const { icon, animation } = assignIconAndAnimation(params.phrase);
	const [isHovered, setIsHovered] = useState(false);

	return (
		<motion.li
			key={params.itemKey}
			className="flex items-center cursor-pointer"
			onMouseEnter={() => setIsHovered(true)}
			onMouseLeave={() => setIsHovered(false)}
		>
			<motion.div
				className="mr-3 flex items-center justify-center"
				style={{ width: "24px", height: "24px" }}
				animate={
					isHovered
						? {
								...animation,
								color: HOVER_COLOR,
							}
						: {
								rotate: 0,
								rotateY: 0,
								scale: 1,
								x: 0,
								y: 0,
								color: "#f87171",
								transition: { type: "spring", stiffness: 300, damping: 10 },
							}
				}
			>
				<FontAwesomeIcon icon={icon} size="lg" className="align-middle" />
			</motion.div>
			<motion.p className="text-gray-300 hover:text-white transition-colors flex-1">
				{formatFeatureText(params.phrase)}
			</motion.p>
		</motion.li>
	);
}
